export class FiltersService {
    private static DEFAULT_PRODUCT_TYPE = 'singles';

    static generateFilterURL({
        basePath,
        filters,
        universe,
        productType,
        expansion,
        sortBy,
        search,
    }: {
        basePath?: string;
        filters: Record<string, string>;
        universe: string;
        productType?: string;
        expansion?: string;
        sortBy?: string;
        search?: string;
    }): string {
        // Initialize the base URL using the provided basePath or an empty string
        let url = basePath ?? '';

        // Filter out empty filter values and the default 'all' value
        const filtered = Object.fromEntries(
            Object.entries(filters).filter(([, v]) => v && v !== 'all'),
        );

        // Create a slug from the filtered entries (e.g., "color-red/size-large")
        const filtersSlug = Object.entries(filtered)
            .map(([key, val]) => `${key}-${val}`)
            .join('/');

        // Build the URL path based on hierarchy: universe > productType > expansion
        if (expansion && productType) {
            // Full path: universe/productType/expansion
            url += `${universe}/${productType}/${expansion}`;
        } else if (expansion) {
            // Expansion without explicit productType uses default product type
            url += `${universe}/${productType || FiltersService.DEFAULT_PRODUCT_TYPE}/${expansion}`;
        } else if (productType) {
            // Path with universe and productType
            url += `${universe}/${productType}`;
        } else if (universe) {
            // Path with just universe
            url += universe;
        } else {
            // Fallback: determine path from current window location
            const segments = window.location.pathname
                .split('/')
                .filter(Boolean);
            if (segments.length === 1) {
                url += `/${segments[0]}`;
            } else if (segments.length === 2) {
                url += `/${segments[0]}/${segments[1]}`;
            } else {
                url += `${universe}/${FiltersService.DEFAULT_PRODUCT_TYPE}`;
            }
        }

        // Append filters path segment if any filters exist
        url = filtersSlug ? `${url}/filters/${filtersSlug}` : url;

        const queryParams = new URLSearchParams();

        if (sortBy) {
            queryParams.set('sort', sortBy);
        }

        if (search?.trim()) {
            queryParams.set('search', search.trim());
        }

        const queryString = queryParams.toString();

        return queryString ? `${url}?${queryString}` : url;
    }

    static generateResetFilterUrl({
        basePath,
        universe,
        productType,
        search,
    }: {
        basePath?: string;
        universe: string;
        productType?: string;
        search?: string;
    }): string {
        const url = basePath ?? '';
        const catalogUrl = productType
            ? `${url}${universe}/${productType}`
            : `${url}${universe}`;
        const normalizedSearch = search?.trim();

        return normalizedSearch
            ? `${catalogUrl}?${new URLSearchParams({ search: normalizedSearch })}`
            : catalogUrl;
    }
}
